import React, { useState, useContext } from 'react';
import { DatePicker, Divider, Typography, AutoComplete, Col, Row } from 'antd';
import { Moment } from 'moment';
import { SearchOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

import Button from '../button/button.component';
import AppContext, { AppContextType } from '../../contexts/app.context';
import { routes } from '../../routes/main.routes';

const { Title } = Typography;

interface DetailsFilterProps {
  rounded?: boolean;
  readonly?: boolean;
}

const RentalDetailsFilterComponent: React.FC<DetailsFilterProps> = ({ rounded }) => {
  const { rentalFromDate, setRentalFromDate, rentalUntilDate, setRentalUntilDate, rentalLocation, setRentalLocation } =
    useContext<AppContextType | null>(AppContext) as AppContextType;
  const navigate = useNavigate();
  const [options, setOptions] = useState<{ value: string }[]>([
    { value: 'Las Vegas' },
    { value: 'London' },
    { value: 'New York' },
    { value: 'Paris' },
  ]);

  const clickHandler = () => {
    navigate(routes.vehicles.path);

    // when we are in vechiles, we want to simulate page refresh so it wont look like it got stuck
    if (window.location.pathname === routes.vehicles.path) {
      setTimeout(() => {
        navigate(0);
      }, 500);
    }
  };

  const onSearch = (searchText: string) => {
    setOptions(!searchText ? [] : options);
  };

  const onFromDateChange = (date: Moment | null) => {
    setRentalFromDate(date);
  };

  const onUntilDateChange = (date: Moment | null) => {
    setRentalUntilDate(date);
  };

  const onLocationSelect = (value: string) => {
    setRentalLocation(value);
  };

  return (
    <StyledRow style={rounded ? { borderRadius: '100px' } : {}}>
      <Col span={24} md={5}>
        <FilterTitle level={5}>Where</FilterTitle>
        <StyledAutoComplete
          bordered={false}
          options={options}
          onSearch={onSearch}
          onSelect={onLocationSelect}
          placeholder="City, airport, address or hotel"
          value={rentalLocation}
        />
      </Col>
      <Col span={0} md={1}>
        <StyledDivider type="vertical" />
      </Col>
      <Col span={24} md={5}>
        <FilterTitle level={5}>From</FilterTitle>
        <FilterDatePicker
          allowClear={false}
          bordered={false}
          inputReadOnly
          format="DD/MM/YYYY"
          onChange={onFromDateChange}
          value={rentalFromDate}
        />
      </Col>
      <Col span={0} md={1}>
        <StyledDivider type="vertical" />
      </Col>
      <Col span={24} md={5}>
        <FilterTitle level={5}>Until</FilterTitle>

        <FilterDatePicker
          allowClear={false}
          bordered={false}
          inputReadOnly
          format="DD/MM/YYYY"
          onChange={onUntilDateChange}
          value={rentalUntilDate}
        />
      </Col>
      <Col span={24} md={7} style={{ textAlign: 'right' }}>
        <ResponsiveSearchButton type="primary" size="large" icon={<SearchOutlined />} onClick={clickHandler}>
          <span className="search-text">Search</span>
        </ResponsiveSearchButton>
      </Col>
    </StyledRow>
  );
};

const ResponsiveSearchButton = styled(Button).attrs({ className: 'responsive-search-button' })`
  // default view of responsive search button

  &.responsive-search-button {
    font-family: var(--font-family-regular);
    font-weight: 600;
    width: 40px;
    height: 40px;
    background: black;
    border-color: black;
    padding: 5px;
  }

  .search-text {
    display: none;
  }

  // minimize view of responsive search button
  @media only screen and (max-width: 768px) {
    &.responsive-search-button {
      width: 100%;
      height: auto;
    }

    .search-text {
      display: inline-block;
    }
  }
`;

const StyledRow = styled(Row)`
  padding: 10px;
  background: #ffffff;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border-radius: 2px;

  @media only screen and (max-width: 768px) {
    margin-top: 15px;

    > div:not(:last-child) {
      padding-top: 10px;
    }
    > div:nth-child(1),
    > div:nth-child(3) {
      border-bottom: 1px solid rgba(0, 0, 0, 0.4);
    }
  }
`;

const FilterDatePicker = styled(DatePicker)`
  width: 100%;
  input:disabled {
    color: black;
    cursor: auto;
  }
`;

const StyledAutoComplete = styled(AutoComplete)`
  width: 100%;
  input:disabled {
    color: black;
    cursor: auto;
  }
`;

const StyledDivider = styled(Divider)`
  && {
    display: inline-block;
    margin-top: 18px;
  }
`;

const FilterTitle = styled(Title).attrs({ className: 'fitler-title' })`
  &.fitler-title {
    font-size: 12px;
    display: block;
    padding-left: 12px;
    margin: 0 0 -3px 0;
  }
`;

export default RentalDetailsFilterComponent;
